import { Component, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FilterComponent } from '@modules/shared/components/filter/filter.component';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { PaginatorComponent } from '@modules/shared/components/paginator/paginator.component';
import { SelectionModel } from '@angular/cdk/collections';
import { PageEvent } from '@angular/material/paginator';
import { UserDataSource } from '@ds/user.ds';
import { UserService } from '@services/auth/user.service';
import { User } from '@models/users/user.model';
import { UserRole } from '@models/users/user-role.enum';

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FilterComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatSortModule,
    MatTableModule,
    PaginatorComponent,
  ],
  providers: [UserService],
  templateUrl: './user-list.page.html',
  styleUrl: './user-list.page.scss',
})
export class UserListPage {
  @ViewChild(FilterComponent) filter!: FilterComponent;
  @ViewChild(PaginatorComponent) paginator!: PaginatorComponent;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  dataSource: UserDataSource;

  displayedColumns: string[] = [
    'select',
    'name',
    'id',
    'creationDate',
    'email',
    'emailVerified',
    'phone',
    'phoneVerified',
    'role',
    'useCase',
    'referredByUser',
    'numHostingEvents',
    'numCoHostingEvents',
    'numAttendingEvents',
  ];

  selection = new SelectionModel<any>(true, []);

  pageSizeOptions: number[] = [5, 10, 15, 20, 50, 100]; // Options for the user to change the page size

  constructor(private userService: UserService) {
    this.dataSource = new UserDataSource(this.userService);
  }

  ngOnInit() {
    // Set the initial page
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if the 'currentPage' or 'products' have changed
    this.refresh();
  }

  ngAfterViewInit() {
    // reset the paginator after sorting
    this.sort.sortChange.subscribe(() => {
      this.resetPageIndex();
      this.refresh();
    });

    this.refresh();
  }

  onFilterChange(event: any) {
    this.resetPageIndex();
    this.refresh();
  }

  onPageChange(event: PageEvent) {
    this.refresh();
  }

  private resetPageIndex() {
    this.paginator.pager.currentPage = 1;
  }

  refresh() {
    const filters = new Map<string, any>();

    if (this.filter?.searchText) {
      filters.set('searchTerm', this.filter.searchText);
    }

    if (!!this.sort.direction) {
      filters.set('sortDirection', this.sort.direction);
      filters.set('sortProperty', this.sort.active);
    }

    this.dataSource.loadUsers(
      filters,
      this.paginator.pager.currentPage !== 0
        ? this.paginator.pager.currentPage
        : 1,
      this.paginator.pageSize || 10,
    );
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource?.data?.forEach((row) => this.selection.select(row));
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource?.data?.length;
    return numSelected === numRows;
  }

  toggleSelection(row: any) {
    this.selection.toggle(row);
  }

  onRowClick(user: User, event: MouseEvent) {}

  protected readonly UserRole = UserRole;
}
